/**
 * IconPicker
 *
 * A searchable visual grid of Lucide icons for selecting block icons.
 * Opens as a Popover from a trigger button showing the current icon.
 */
import React, { useState, useMemo } from 'react';
import {
  // Arrows & Navigation
  ArrowRight, ArrowLeft, ArrowUp, ArrowDown, ChevronRight, ExternalLink,
  // Finance & Business
  TrendingUp, TrendingDown, DollarSign, BarChart3, BarChart2, PieChart, LineChart,
  Percent, CreditCard, Wallet, Banknote, Receipt, Calculator,
  // Buildings & Real Estate
  Building, Building2, Home, MapPin, Globe, Map,
  // Security & Trust
  Shield, ShieldCheck, Lock, Key, CheckCircle, CheckCircle2, Award, Star,
  // People & Social
  User, Users, UserCheck, Heart, Handshake, MessageCircle,
  // Tech & Product
  Zap, Cpu, Server, Database, Code2, Layers, Settings, Target,
  // Media & Content
  Image, Video, FileText, Download, Upload, Link, BookOpen, Newspaper,
  // Status & Alerts
  AlertCircle, Info, XCircle, Clock, Calendar, Bell, Loader,
  // Clear
  X,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';

/* ------------------------------------------------------------------ */
/*  Icon map                                                           */
/* ------------------------------------------------------------------ */

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  ArrowRight, ArrowLeft, ArrowUp, ArrowDown, ChevronRight, ExternalLink,
  TrendingUp, TrendingDown, DollarSign, BarChart3, BarChart2, PieChart, LineChart,
  Percent, CreditCard, Wallet, Banknote, Receipt, Calculator,
  Building, Building2, Home, MapPin, Globe, Map,
  Shield, ShieldCheck, Lock, Key, CheckCircle, CheckCircle2, Award, Star,
  User, Users, UserCheck, Heart, Handshake, MessageCircle,
  Zap, Cpu, Server, Database, Code2, Layers, Settings, Target,
  Image, Video, FileText, Download, Upload, Link, BookOpen, Newspaper,
  AlertCircle, Info, XCircle, Clock, Calendar, Bell, Loader,
};

const ICON_NAMES = Object.keys(ICON_MAP);

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface IconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function IconPicker({ value, onChange }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ICON_NAMES;
    return ICON_NAMES.filter((name) => name.toLowerCase().includes(q));
  }, [query]);

  const CurrentIcon = value ? ICON_MAP[value] : null;

  const handleSelect = (name: string) => {
    onChange(name);
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-md hover:border-primary transition-colors text-sm"
        >
          {CurrentIcon ? (
            <>
              <CurrentIcon className="h-5 w-5 text-foreground shrink-0" />
              <span className="text-foreground">{value}</span>
            </>
          ) : (
            <span className="text-muted-foreground">Select icon</span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-72 p-3" align="start">
        {/* Search */}
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search icons..."
          className="h-8 text-xs mb-2"
          autoFocus
        />

        {/* Clear link */}
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive mb-2 transition-colors"
          >
            <X className="h-3 w-3" />
            Clear selection
          </button>
        )}

        {/* Icon grid */}
        {filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">
            No icons match "{query}"
          </p>
        ) : (
          <div className="grid grid-cols-6 gap-1 max-h-48 overflow-y-auto p-1">
            {filtered.map((name) => {
              const IconComp = ICON_MAP[name];
              const isSelected = name === value;
              return (
                <button
                  key={name}
                  type="button"
                  title={name}
                  onClick={() => handleSelect(name)}
                  className={`p-2 rounded flex items-center justify-center transition-colors ${
                    isSelected
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-primary/20 text-foreground cursor-pointer'
                  }`}
                >
                  <IconComp className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
